import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import { CardElement, useElements, useStripe } from "@stripe/react-stripe-js";
import dollar from "../assets/images/icons/dollar.svg";
import searchicon from "../assets/images/icons/search.png";
import greenGlobal from "../assets/images/icons/green-global.png";
import walletIcon from "../assets/images/icons/greenwallet.png";
import paymentsuccess from "../assets/images/icons/Payment-success.svg";
import paymentfailed from "../assets/images/icons/retry-payment.svg";
import Retryicon from "../assets/images/icons/refresh-right-square.svg";
import profileimg from "../assets/profile.png";
import { getUser } from "../utils/auth";
import { authHeaders, formatPrice } from "./becomesell/LiveShoppingParts";

const API_BASE_URL = (process.env.REACT_APP_API_BASE_URL || "").replace(/\/$/, "");

const formatDateTime = (value) =>
  new Date(value).toLocaleString("en-US", { dateStyle: "medium", timeStyle: "short" });

const requestLive = async (path, options = {}) => {
  const res = await fetch(`${API_BASE_URL}${path}`, { ...options, headers: authHeaders() });
  const data = await res.json();
  if (!res.ok || !data.success) throw new Error(data.message || "Something went wrong");
  return data.result;
};

/**
 * Donate money (wallet or a card entered through Stripe; card details are never stored) or send a virtual gift
 * (always paid from the wallet) to a live donation stream.
 */
// Readable order number from the sequential row id, e.g. DON-000042
const orderNumber = (prefix, id) => (id ? `${prefix}-${String(id).padStart(6, "0")}` : null);

const GoLiveDonationgiftModal = ({ isOpen, onClose, logo, closeIcon, stream }) => {
  const navigate = useNavigate();
  const stripe = useStripe();
  const elements = useElements();

  const [step, setStep] = useState("amount"); // amount | payment | result
  const [sheet, setSheet] = useState(null);
  const [gifts, setGifts] = useState([]);
  const [giftSearch, setGiftSearch] = useState("");
  const [amount, setAmount] = useState(null);
  const [customAmount, setCustomAmount] = useState("");
  const [gift, setGift] = useState(null);
  const [method, setMethod] = useState("wallet");
  const [submitting, setSubmitting] = useState(false);
  const [result, setResult] = useState(null);

  const streamId = stream?.stream_id;
  const hostName = [stream?.user?.first_name, stream?.user?.last_name].filter(Boolean).join(" ");
  const giftsEnabled = stream?.gifts_enabled !== false;

  useEffect(() => {
    if (!isOpen || !streamId) return;
    setStep("amount");
    setAmount(null);
    setCustomAmount("");
    setGift(null);
    setGiftSearch("");
    setMethod("wallet");
    setResult(null);

    requestLive(`/live/${streamId}/donation/bottom-sheet`)
      .then(setSheet)
      .catch((error) => {
        toast.error(error.message);
        onClose();
      });
    if (giftsEnabled) {
      requestLive("/live/gifts")
        .then((data) => setGifts(data.gifts || []))
        .catch(() => setGifts([]));
    }
  }, [isOpen, streamId, giftsEnabled, onClose]);

  if (!isOpen || !stream) return null;

  const total = gift ? gift.coinValue : Number(customAmount) || amount || 0;
  const walletBalance = Number(sheet?.wallet_balance) || 0;
  const minGiftCoins = Number(stream.min_gift_coins) || 0;
  const visibleGifts = gifts.filter(
    (g) => g.coinValue >= minGiftCoins && g.name.toLowerCase().includes(giftSearch.trim().toLowerCase())
  );
  const payMethod = gift ? "wallet" : method;
  const insufficientBalance = payMethod === "wallet" && total > walletBalance;

  const selectAmount = (value) => {
    setAmount(value);
    setCustomAmount("");
    setGift(null);
  };

  const handleCustomAmount = (e) => {
    const { value } = e.target;
    if (value === "" || /^\d{0,7}(\.\d{0,2})?$/.test(value)) {
      setCustomAmount(value);
      setAmount(null);
      setGift(null);
    }
  };

  const selectGift = (selected) => {
    setGift(selected);
    setAmount(null);
    setCustomAmount("");
  };

  const handlePay = async () => {
    setSubmitting(true);
    const receipt = { total, date: new Date() };
    try {
      if (gift) {
        const data = await requestLive(`/live/${streamId}/gift`, {
          method: "POST",
          body: JSON.stringify({ giftId: gift.id, coinValue: gift.coinValue }),
        });
        Object.assign(receipt, { orderNo: orderNumber("GFT", data.transaction.id), reference: data.transaction.transactionId, method: "Wallet" });
      } else if (payMethod === "wallet") {
        const data = await requestLive(`/live/${streamId}/donation`, {
          method: "POST",
          body: JSON.stringify({ amount: total, payment_method: "wallet" }),
        });
        Object.assign(receipt, { orderNo: orderNumber("DON", data.donation.id), method: "Wallet" });
      } else {
        if (!stripe || !elements) throw new Error("Card payments are not available right now");
        const { error, paymentMethod } = await stripe.createPaymentMethod({
          type: "card",
          card: elements.getElement(CardElement),
        });
        if (error) throw new Error(error.message);

        const data = await requestLive(`/live/${streamId}/donation`, {
          method: "POST",
          body: JSON.stringify({ amount: total, payment_method: "card", payment_method_id: paymentMethod.id }),
        });
        const { error: confirmError, paymentIntent } = await stripe.confirmCardPayment(
          data.payment_intent.client_secret
        );
        if (confirmError) throw new Error(confirmError.message);
        if (paymentIntent.status !== "succeeded") throw new Error("The payment was not completed");

        Object.assign(receipt, {
          orderNo: orderNumber("DON", data.donation.id),
          reference: paymentIntent.id,
          method: `Credit Card (**** ${paymentMethod.card.last4})`,
        });
      }
      setResult({ ...receipt, success: true });
    } catch (error) {
      setResult({ ...receipt, success: false, error: error.message, method: payMethod === "wallet" ? "Wallet" : "Credit Card" });
    } finally {
      setSubmitting(false);
      setStep("result");
    }
  };

  const senderName = (() => {
    const user = getUser();
    return [user?.first_name, user?.last_name].filter(Boolean).join(" ") || user?.username || "";
  })();

  const header = (
    <div className="grid grid-cols-1 gap-6 px-8 pt-6 md:grid-cols-[220px_1fr]">
      <div>
        <p className="mb-2 text-sm font-semibold">Creator Name:</p>
        <div className="flex items-center gap-2">
          <img
            src={stream.user?.profile_picture_url || profileimg}
            alt=""
            className="h-10 w-10 rounded-full border-2 border-[#BE1F2E] object-cover"
          />
          <div>
            <p className="text-sm font-semibold">{hostName}</p>
            {stream.started_at && <p className="text-[10px] text-gray-500">{formatDateTime(stream.started_at)}</p>}
          </div>
        </div>
      </div>
      <div>
        <p className="mb-2 text-sm font-semibold">Donation / Gift Summary</p>
        <p className="text-lg font-semibold">{stream.title}</p>
        <p className="flex items-center gap-1 text-[10px] text-gray-500">
          {stream.started_at && formatDateTime(stream.started_at)}
          <span className="flex items-center gap-1 text-[#095228]">
            | <img src={greenGlobal} alt="" className="h-3 w-3" />
            {stream.visibility === "private" || (stream.view_audience && stream.view_audience !== "public") ? "Private" : "Public"}
          </span>
        </p>
      </div>
    </div>
  );

  const renderResult = () => (
    <div className="flex flex-col items-center px-8 py-6 text-center">
      <img src={result.success ? paymentsuccess : paymentfailed} alt="" className="h-28 w-28" />
      <h3 className={`mt-2 text-lg font-semibold ${result.success ? "text-black" : "text-[#BE1F2E]"}`}>
        {result.success ? "Thank You for Supporting!" : "Payment Failed"}
      </h3>
      <p className="text-sm text-gray-600">
        {result.success ? (
          <>
            Your {gift ? "gift" : "donation"} has been successfully sent to <span className="font-semibold">{hostName}</span>
          </>
        ) : (
          result.error || "Unfortunately, we couldn't process your payment."
        )}
      </p>
      <p className="mt-4 text-sm text-gray-600">Total Payment</p>
      <p className="text-4xl font-semibold">{formatPrice(result.total)}</p>
      <p className="text-xs font-medium">{formatDateTime(result.date)}</p>

      <div className="mt-6 grid w-full max-w-2xl grid-cols-1 gap-3 text-left sm:grid-cols-2">
        {[
          ["Order No", result.orderNo || "-"],
          ["Payment Ref No.", result.success ? result.reference || result.orderNo : "Failed"],
          ["Sender Name", senderName],
          ["Payment Method", result.method],
        ].map(([label, value]) => (
          <div key={label} className="rounded-xl border border-[#EDEDED] p-3">
            <p className="text-[11px] text-gray-500">{label}</p>
            <p className={`break-all text-sm ${label === "Payment Ref No." && !result.success ? "text-[#BE1F2E]" : ""}`}>{value}</p>
          </div>
        ))}
      </div>

      {result.success ? (
        <button onClick={onClose} className="mt-8 rounded-full bg-[#095228] px-16 py-3 font-semibold text-white">
          Continue Watching Live
        </button>
      ) : (
        <button
          onClick={() => setStep("payment")}
          className="mt-8 flex items-center gap-2 rounded-full bg-[#BE1F2E] px-8 py-2 font-semibold text-white"
        >
          <img src={Retryicon} alt="" className="h-5 w-5" />
          Retry Payment
        </button>
      )}
    </div>
  );

  const amountStep = (
    <div className="flex h-full flex-col rounded-2xl bg-white p-4">
      <p className="mb-3 font-semibold">Choose Your Amount</p>
      <div className="flex flex-wrap items-center gap-3">
        {(sheet?.preset_amounts || []).map((value) => (
          <button
            key={value}
            onClick={() => selectAmount(value)}
            className={`flex items-center gap-2 rounded-full border px-3 py-1 font-medium transition ${
              amount === value ? "border-[#FF7A51] bg-[#FFB800]" : "border-[#FFE7A3] bg-[#FFE7A3] opacity-70 hover:opacity-100"
            }`}
          >
            <img src={dollar} alt="" className="h-4 w-4" />
            {value}
          </button>
        ))}
        <div
          className={`flex h-11 items-center gap-2 rounded-xl border px-3 ${
            customAmount ? "border-[#095228] bg-[#F3F8F5]" : "border-gray-300"
          }`}
        >
          <span className="text-gray-500">$</span>
          <input
            inputMode="decimal"
            value={customAmount}
            onChange={handleCustomAmount}
            placeholder="0"
            className="w-24 bg-transparent text-right outline-none"
          />
        </div>
      </div>

      {giftsEnabled && (
        <>
          <hr className="my-4" />
          <div className="flex flex-wrap items-center justify-between gap-3">
            <p className="font-semibold">Choose Your Gift</p>
            <div className="relative">
              <img src={searchicon} alt="" className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2" />
              <input
                type="search"
                value={giftSearch}
                onChange={(e) => setGiftSearch(e.target.value)}
                placeholder="Search for Gift"
                className="rounded-full bg-gray-100 py-2 pl-9 pr-3 text-sm focus:outline-none"
              />
            </div>
          </div>
          <div className="mt-3 grid max-h-[220px] grid-cols-3 gap-3 overflow-y-auto sm:grid-cols-4">
            {visibleGifts.map((g) => (
              <button
                key={g.id}
                onClick={() => selectGift(g)}
                className={`flex flex-col items-center rounded-xl border-2 p-2 transition ${
                  gift?.id === g.id ? "border-[#095228] bg-green-50" : "border-transparent hover:bg-gray-50"
                }`}
              >
                <span className="text-3xl">{g.emoji || "🎁"}</span>
                <span className="text-xs font-medium">{g.name}</span>
                <span className="flex items-center gap-1 text-xs font-semibold text-[#E8A317]">
                  <img src={dollar} alt="" className="h-3 w-3" />
                  {g.coinValue}
                </span>
              </button>
            ))}
            {visibleGifts.length === 0 && <p className="col-span-full py-6 text-center text-sm text-gray-400">No gifts found</p>}
          </div>
        </>
      )}
    </div>
  );

  const paymentStep = (
    <div className="space-y-4">
      <label
        className={`block rounded-2xl bg-[#F4F4F4] p-4 ${gift ? "opacity-50" : "cursor-pointer"}`}
      >
        <div className="mb-3 flex items-center gap-2">
          <input
            type="radio"
            checked={payMethod === "card"}
            disabled={!!gift}
            onChange={() => setMethod("card")}
            className="accent-[#095228]"
          />
          <span className="text-sm font-semibold">Pay with Card</span>
        </div>
        {gift ? (
          <p className="text-xs text-gray-500">Gifts are paid from your wallet.</p>
        ) : (
          payMethod === "card" && (
            <div className="rounded-xl border border-[#095228] bg-white px-4 py-3">
              <CardElement options={{ hidePostalCode: true, style: { base: { fontSize: "15px" } } }} />
            </div>
          )
        )}
      </label>

      <label className="block cursor-pointer rounded-2xl bg-[#F4F4F4] p-4">
        <div className="mb-3 flex items-center gap-2">
          <input
            type="radio"
            checked={payMethod === "wallet"}
            onChange={() => setMethod("wallet")}
            className="accent-[#095228]"
          />
          <span className="text-sm font-semibold">Wallet</span>
        </div>
        <div className="flex flex-wrap items-center justify-between gap-4">
          <div className="flex items-center gap-4 rounded-xl bg-white px-4 py-3">
            <img src={walletIcon} alt="" className="h-10 w-10" />
            <span className="text-2xl font-semibold">{formatPrice(walletBalance)}</span>
          </div>
          <button
            type="button"
            onClick={() => navigate("/wallet")}
            className="rounded-full bg-[#095228] px-8 py-2 text-sm text-white"
          >
            Top Up Wallet
          </button>
        </div>
        {insufficientBalance && (
          <p className="mt-2 text-xs text-[#BE1F2E]">Your wallet balance is too low. Top up or pay with a card.</p>
        )}
      </label>
    </div>
  );

  return (
    <div className="get-verified popup-share fixed inset-0 z-50 flex w-full items-center justify-center bg-[#000] bg-opacity-60 backdrop-blur-md">
      <div className="border-popup relative max-h-[90vh] w-full max-w-5xl overflow-y-auto rounded-2xl bg-white">
        <div className="flex justify-between p-6 pb-0">
          <img src={logo} alt="Nyawoo" className="w-28" />
          <button onClick={onClose} disabled={submitting}>
            <img src={closeIcon} alt="close" className="h-8 w-8" />
          </button>
        </div>

        {header}

        {step === "result" ? (
          renderResult()
        ) : (
          <div className="grid grid-cols-1 gap-6 p-8 lg:grid-cols-[1fr_300px]">
            <div className="rounded-2xl bg-[#F4F4F4] p-3">{step === "amount" ? amountStep : paymentStep}</div>

            <div className="h-fit rounded-2xl border p-5">
              <h3 className="mb-4 text-lg font-semibold">Order Summary</h3>
              <div className="flex justify-between text-sm">
                <span className="text-gray-500">Subtotal</span>
                <span className="font-semibold">{formatPrice(total)}</span>
              </div>
              <hr className="my-3" />
              <div className="flex justify-between text-lg font-semibold">
                <span>Total</span>
                <span>{formatPrice(total)}</span>
              </div>
              {step === "amount" ? (
                <button
                  onClick={() => setStep("payment")}
                  disabled={!(total > 0) || !sheet}
                  className="mt-5 flex h-12 w-full items-center justify-center gap-2 rounded-full bg-[#095228] text-white disabled:opacity-50"
                >
                  Donate <span>→</span>
                </button>
              ) : (
                <>
                  <button
                    onClick={handlePay}
                    disabled={submitting || insufficientBalance}
                    className="mt-5 flex h-12 w-full items-center justify-center gap-2 rounded-full bg-[#095228] text-white disabled:opacity-50"
                  >
                    {submitting ? "Processing..." : <>Pay Now <span>→</span></>}
                  </button>
                  <button
                    onClick={() => setStep("amount")}
                    disabled={submitting}
                    className="mt-3 w-full text-sm text-gray-500 hover:underline"
                  >
                    Go Back
                  </button>
                </>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default GoLiveDonationgiftModal;
